import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    IconButton,
    Alert,
    Snackbar,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MergeIcon from "@mui/icons-material/Merge";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { ToolPageShell } from "./ToolPageShell";
import { TOOLS, formatFileSize } from "../util/config";
import { colors } from "../theme/theme";
import { pdfApi, statsApi } from "../api/client";

const tool = TOOLS.find((t) => t.id === "pdf-merge");

export const PdfMergePage = () => {
    const [pdfFiles, setPdfFiles] = useState([]);
    const [isMerging, setIsMerging] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

    const onDrop = useCallback((acceptedFiles) => {
        const validPdfs = acceptedFiles.filter((f) => f.type === "application/pdf");
        if (validPdfs.length > 0) {
            setPdfFiles((prev) => [...prev, ...validPdfs]);
            showSnackbar(`${validPdfs.length} PDF file(s) added`, "success");
        } else {
            showSnackbar("Please upload valid PDF files", "error");
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "application/pdf": [".pdf"] },
        multiple: true,
    });

    const movePdfUp = (index) => {
        if (index === 0) return showSnackbar("File is already at the top", "info");
        const newFiles = [...pdfFiles];
        [newFiles[index], newFiles[index - 1]] = [newFiles[index - 1], newFiles[index]];
        setPdfFiles(newFiles);
    };

    const mergePdfs = async () => {
        if (pdfFiles.length < 2) return showSnackbar("Upload at least 2 PDFs", "error");
        setIsMerging(true);
        try {
            const blob = await pdfApi.merge(pdfFiles);
            statsApi.logExport(tool.id, blob.size);
            showSnackbar("PDFs merged and downloaded!", "success");
        } catch (error) {
            console.error(error);
            showSnackbar("Failed to merge PDFs", "error");
        } finally {
            setIsMerging(false);
        }
    };

    const removePdf = (index) => setPdfFiles((prev) => prev.filter((_, i) => i !== index));
    const clearAllPdfs = () => setPdfFiles([]);

    return (
        <ToolPageShell
            tool={tool}
            tips={[
                "Order matters — files are merged top to bottom.",
                "Click any file to move it one position up.",
                "Add as many PDFs as you need; no page-count limit.",
            ]}
        >
            <Box
                {...getRootProps()}
                sx={{
                    border: "2px dashed",
                    borderColor: isDragActive ? "primary.main" : colors.border,
                    borderRadius: 2,
                    padding: 6,
                    textAlign: "center",
                    cursor: "pointer",
                    bgcolor: isDragActive ? colors.accentSoft : colors.surfaceAlt,
                    transition: "all 0.2s",
                    mb: 6,
                    "&:hover": { borderColor: "primary.main", bgcolor: colors.accentSoft },
                }}
            >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 44, color: isDragActive ? "primary.main" : colors.textMuted, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {isDragActive ? "Drop your PDFs" : "Drag & drop PDFs"}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.textMuted }}>
                    or click to browse
                </Typography>
            </Box>

            {pdfFiles.length > 0 && (
                <Box mb={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography sx={{ fontWeight: 600 }}>
                            Files to merge ({pdfFiles.length})
                        </Typography>
                        <Button size="small" variant="outlined" onClick={clearAllPdfs} startIcon={<CloseIcon />}>
                            Clear All
                        </Button>
                    </Box>
                    <List sx={{ maxHeight: 320, overflow: "auto", bgcolor: colors.surfaceAlt, borderRadius: 2, p: 0, border: `1px solid ${colors.border}` }}>
                        {pdfFiles.map((file, index) => (
                            <ListItem
                                key={index}
                                onClick={() => movePdfUp(index)}
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        onClick={(e) => { e.stopPropagation(); removePdf(index); }}
                                        sx={{ color: "error.main" }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                }
                                sx={{
                                    borderBottom: `1px solid ${colors.border}`,
                                    cursor: index > 0 ? "pointer" : "default",
                                    "&:hover": index > 0 ? { bgcolor: colors.surface } : {},
                                    "&:last-child": { borderBottom: "none" },
                                }}
                            >
                                <ListItemIcon><PictureAsPdfIcon sx={{ color: "error.main" }} /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box component="span" sx={{ fontWeight: 500 }}>
                                            <Box component="span" sx={{ fontFamily: '"JetBrains Mono"', color: colors.textMuted, mr: 1 }}>
                                                {String(index + 1).padStart(2, "0")}
                                            </Box>
                                            {file.name}
                                            {index === 0 && (
                                                <Box component="span" sx={{ ml: 1, px: 1, py: 0.25, bgcolor: tool.accent, color: "white", fontSize: 9, borderRadius: 0.5, fontFamily: '"JetBrains Mono"', letterSpacing: "0.08em" }}>
                                                    TOP
                                                </Box>
                                            )}
                                        </Box>
                                    }
                                    secondary={formatFileSize(file.size)}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}

            {pdfFiles.length > 0 && (
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={mergePdfs}
                    disabled={isMerging || pdfFiles.length < 2}
                    startIcon={isMerging ? <CircularProgress size={20} color="inherit" /> : <MergeIcon />}
                >
                    {isMerging ? "Merging..." : `Merge ${pdfFiles.length} PDF${pdfFiles.length > 1 ? "s" : ""}`}
                </Button>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </ToolPageShell>
    );
};
